import {
  useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState,
  type ChangeEvent, type FocusEvent, type KeyboardEvent, type Ref, type ReactNode, type SyntheticEvent, type UIEvent
} from 'react';
import { useMergedRef } from '@repletoryreach/sdk-utils/hooks';
import { Portal } from '../../core/Portal/Portal.js';
import { computePosition } from '../../overlays/floating-positioner.js';
import {
  DEFAULT_CLOSING, DEFAULT_TRIGGER, filterSuggestions, findOpenToken, groupSuggestions, insertToken, segmentTokens,
  type TokenSuggestion
} from './tokens.js';
import './TokenInput.css';

type Control = HTMLInputElement | HTMLTextAreaElement;

/** What both `TokenInput` and `TokenTextarea` hand the shared field. */
export interface TokenFieldOptions {
  tokens: readonly TokenSuggestion[];
  trigger?: string;
  closing?: string;
  /** One line at the foot of the list, for tokens that take a fallback. */
  fallbackHint?: string;
}

interface TokenFieldCoreProps extends TokenFieldOptions {
  multiline: boolean;
  /** From `InputWrapper`. */
  inputId: string;
  describedBy?: string;
  invalid: boolean;
  controlClass: string;
  forwardedRef: Ref<Control>;
  /** Everything else the caller gave for the native element. */
  controlProps: Record<string, unknown>;
}

/**
 * Writes a value the way a person typing would, so React's `onChange` fires
 * for a controlled field. React tracks the last value it set on the node and
 * only reports an `input` event whose value differs; assigning through the
 * prototype's setter bypasses that record, and the event then reaches the
 * caller's handler with the new text, exactly as a keystroke does.
 */
function writeNativeValue(el: Control, value: string): void {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) setter.call(el, value); else el.value = value;
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * The shared field: a native control whose tokens are painted, with a list of
 * the ones the person may insert opening under it the moment they type `{{`.
 *
 * <p>The list never takes focus. Every key that drives it (the arrows, Enter,
 * Tab, Escape) is read from the control's own keydown, every click on it
 * prevents the mousedown that would move focus, and it closes on blur. Focus
 * staying in the field is what keeps the caret, the IME composition and the
 * undo stack exactly where the person left them.</p>
 */
export function TokenFieldCore(props: TokenFieldCoreProps): ReactNode {
  const {
    multiline, tokens, trigger = DEFAULT_TRIGGER, closing = DEFAULT_CLOSING, fallbackHint,
    inputId, describedBy, invalid, controlClass, forwardedRef, controlProps
  } = props;

  const {
    value, defaultValue, onChange, onKeyDown, onSelect, onKeyUp, onClick, onFocus, onBlur, onScroll, disabled,
    style, className, ...rest
  } = controlProps as {
    value?: unknown; defaultValue?: unknown;
    onChange?: (e: ChangeEvent<Control>) => void;
    onKeyDown?: (e: KeyboardEvent<Control>) => void;
    onKeyUp?: (e: KeyboardEvent<Control>) => void;
    onSelect?: (e: SyntheticEvent<Control>) => void;
    onClick?: (e: React.MouseEvent<Control>) => void;
    onFocus?: (e: FocusEvent<Control>) => void;
    onBlur?: (e: FocusEvent<Control>) => void;
    onScroll?: (e: UIEvent<Control>) => void;
    disabled?: boolean;
    style?: React.CSSProperties;
    className?: string;
  };

  const isControlled = value !== undefined;
  const [inner, setInner] = useState<string>(() => (defaultValue == null ? '' : String(defaultValue)));
  const text = isControlled ? (value == null ? '' : String(value)) : inner;

  const controlRef = useRef<Control | null>(null);
  const mirrorRef = useRef<HTMLDivElement | null>(null);
  const mergedRef = useMergedRef<Control>(forwardedRef, controlRef);
  const listId = useId();

  const [caret, setCaret] = useState<number | null>(null);
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(0);
  /** Set by Escape: the open token the person dismissed, so it stays closed until they type on. */
  const [dismissed, setDismissed] = useState<{ start: number; query: string } | null>(null);
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  const [menuEl, setMenuEl] = useState<HTMLDivElement | null>(null);
  /** Where the caret must go once React has committed an inserted token. */
  const pendingCaret = useRef<{ value: string; caret: number } | null>(null);

  // ── where the caret is, and whether it is inside an open token ──────────

  const openAt = useMemo(
    () => (caret === null ? null : findOpenToken(text, caret, trigger, closing)),
    [text, caret, trigger, closing]
  );
  const visible = useMemo(() => {
    if (!openAt || tokens.length === 0) return [];
    if (dismissed && dismissed.start === openAt.start && dismissed.query === openAt.query) return [];
    // Digits after the braces are a Meta slot being typed, not a search.
    if (/^\d+$/.test(openAt.query.trim())) return [];
    return filterSuggestions(tokens, openAt.query);
  }, [openAt, tokens, dismissed]);
  const isOpen = focused && !disabled && visible.length > 0;
  const groups = useMemo(() => groupSuggestions(visible), [visible]);

  useEffect(() => { setActive(0); }, [openAt?.start, openAt?.query]);
  useEffect(() => { if (active >= visible.length) setActive(Math.max(0, visible.length - 1)); }, [visible.length, active]);

  const readCaret = useCallback((el: Control | null) => {
    if (!el) return;
    setCaret(el.selectionStart ?? el.value.length);
  }, []);

  // ── keeping the mirror on the control's pixels ─────────────────────────

  const sync = useCallback(() => {
    const el = controlRef.current;
    const m = mirrorRef.current;
    if (!el || !m) return;
    m.style.transform = `translate(${-el.scrollLeft}px, ${-el.scrollTop}px)`;
    // A textarea with a scrollbar wraps its text in a narrower box than the
    // mirror's `width: 100%`, so the mirror is cut to the control's own
    // content width whenever that width is known.
    if (multiline && el.clientWidth > 0) {
      const cs = window.getComputedStyle(el);
      const borders = (parseFloat(cs.borderLeftWidth) || 0) + (parseFloat(cs.borderRightWidth) || 0);
      m.style.width = `${el.clientWidth + borders}px`;
    }
  }, [multiline]);

  useLayoutEffect(sync);
  useEffect(() => {
    const el = controlRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sync]);

  // Once the inserted value has landed on the node, put the caret after it.
  useLayoutEffect(() => {
    const pending = pendingCaret.current;
    const el = controlRef.current;
    if (!pending || !el) return;
    if (el.value !== pending.value) return;
    pendingCaret.current = null;
    try { el.setSelectionRange(pending.caret, pending.caret); } catch { /* not a selectable type */ }
    setCaret(pending.caret);
    sync();
  }, [text, sync]);

  // ── the list's position under the field ────────────────────────────────

  useLayoutEffect(() => {
    if (!isOpen || !menuEl || !controlRef.current) return;
    const measure = () => {
      const anchor = controlRef.current;
      if (!anchor) return;
      const a = anchor.getBoundingClientRect();
      const m = menuEl.getBoundingClientRect();
      const pos = computePosition(a, { width: m.width, height: m.height }, { placement: 'bottom-start', offset: 4 });
      setCoords({ x: pos.x, y: pos.y });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [isOpen, menuEl, visible.length]);

  useEffect(() => {
    if (!isOpen || !menuEl) return;
    menuEl.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, isOpen, menuEl]);

  // ── inserting ──────────────────────────────────────────────────────────

  const commit = useCallback((s: TokenSuggestion) => {
    const el = controlRef.current;
    if (!el || !openAt || caret === null) return;
    const next = insertToken(text, openAt.start, caret, s.token, trigger, closing);
    pendingCaret.current = next;
    if (!isControlled) setInner(next.value);
    writeNativeValue(el, next.value);
    setDismissed(null);
    // An uncontrolled field, or a controlled one whose owner accepted the
    // change synchronously, has the value on the node already.
    if (el.value === next.value) {
      try { el.setSelectionRange(next.caret, next.caret); } catch { /* not a selectable type */ }
      setCaret(next.caret);
      pendingCaret.current = null;
    }
  }, [openAt, caret, text, trigger, closing, isControlled]);

  // ── the control's events ───────────────────────────────────────────────

  const handleChange = (e: ChangeEvent<Control>) => {
    if (!isControlled) setInner(e.currentTarget.value);
    readCaret(e.currentTarget);
    onChange?.(e);
  };

  const handleKeyDown = (e: KeyboardEvent<Control>) => {
    if (isOpen) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(visible.length - 1, i + 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const s = visible[active];
        if (s) { e.preventDefault(); commit(s); return; }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (openAt) setDismissed({ start: openAt.start, query: openAt.query });
        return;
      }
    }
    onKeyDown?.(e);
  };

  const activeId = isOpen && visible[active] ? `${listId}-${active}` : undefined;

  const segments = useMemo(() => segmentTokens(text, trigger, closing), [text, trigger, closing]);

  const menu = isOpen ? (
    <Portal>
      <div
        ref={setMenuEl}
        id={listId}
        role="listbox"
        className="rr-token-menu"
        style={{ left: coords?.x ?? -9999, top: coords?.y ?? -9999 }}
        // Focus stays in the field: nothing here may take it.
        onMouseDown={(e) => e.preventDefault()}
        data-rr-token-menu
      >
        {groups.map(({ group, items }) => (
          <div key={group ?? ''} role="group" aria-label={group}>
            {group ? <div className="rr-token-menu__group" role="presentation">{group}</div> : null}
            {items.map(({ item, index }) => (
              <div
                key={item.token}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === active}
                data-index={index}
                data-active={index === active || undefined}
                className="rr-token-menu__option"
                onMouseEnter={() => setActive(index)}
                onClick={() => commit(item)}
              >
                <div className="rr-token-menu__row">
                  <span className="rr-token-menu__label">{item.label}</span>
                  <span className="rr-token-menu__token">{`${trigger}${item.token}${closing}`}</span>
                </div>
                {item.description ? <span className="rr-token-menu__description">{item.description}</span> : null}
              </div>
            ))}
          </div>
        ))}
        {fallbackHint ? <div className="rr-token-menu__hint" role="presentation">{fallbackHint}</div> : null}
      </div>
    </Portal>
  ) : null;

  const mirror = (
    <div className="rr-token-field__backdrop" aria-hidden>
      <div ref={mirrorRef} className={`${controlClass} rr-token-field__mirror`}>
        {segments.map((s, i) => s.kind === 'text'
          ? <span key={i}>{s.text}</span>
          : <span key={i} className={`rr-token ${s.slot ? 'rr-token--slot' : 'rr-token--attr'}`}>{s.text}</span>)}
        {'\u200b'}
      </div>
    </div>
  );

  const shared = {
    ref: mergedRef as unknown as Ref<HTMLInputElement> & Ref<HTMLTextAreaElement>,
    id: inputId,
    className: [controlClass, 'rr-token-field__control', className].filter(Boolean).join(' '),
    style,
    disabled,
    'aria-invalid': invalid || undefined,
    'aria-describedby': describedBy,
    'aria-autocomplete': 'list' as const,
    'aria-controls': isOpen ? listId : undefined,
    'aria-activedescendant': activeId,
    autoComplete: 'off',
    spellCheck: false,
    ...(isControlled ? { value: text } : { defaultValue: inner }),
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onKeyUp: (e: KeyboardEvent<Control>) => { readCaret(e.currentTarget); onKeyUp?.(e); },
    onClick: (e: React.MouseEvent<Control>) => { readCaret(e.currentTarget); onClick?.(e); },
    onSelect: (e: SyntheticEvent<Control>) => { readCaret(e.currentTarget); onSelect?.(e); },
    onFocus: (e: FocusEvent<Control>) => { setFocused(true); readCaret(e.currentTarget); onFocus?.(e); },
    onBlur: (e: FocusEvent<Control>) => { setFocused(false); onBlur?.(e); },
    onScroll: (e: UIEvent<Control>) => { sync(); onScroll?.(e); }
  };

  return (
    <div className={`rr-token-field${multiline ? ' rr-token-field--multiline' : ''}`} data-disabled={disabled || undefined}>
      {mirror}
      {multiline
        ? <textarea {...(rest as object)} {...shared} />
        : <input {...(rest as object)} {...shared} />}
      {menu}
    </div>
  );
}
